import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingV15I2vParams {
  data?: {
    /**
     * 输入图像URL或Base64编码
     */
    image: string;
    /**
     * 尾帧图像URL或Base64编码
     */
    image_tail?: string;
    /**
     * 视频生成提示词，长度不超过2500字符
     */
    prompt?: string;
    /**
     * 视频时长，可选值：'5'、'10'
     * @values 5 | 10
     */
    duration?: '5' | '10';
    /**
     * 视频宽高比，可选值：'16:9'、'9:16'、'1:1'
     * @values 16:9 | 9:16 | 1:1
     */
    aspect_ratio?: '16:9' | '9:16' | '1:1';
    /**
     * 运动强度/CFG缩放，默认值为0.5
     */
    cfg_scale?: number;
    /**
     * 随机种子
     */
    seed?: number;
    /**
     * 负面提示词
     */
    negative_prompt?: string;
    /**
     * 生成模式，可选值：'std'、'pro'，默认为'std'
     * @default "std"
     * @values std | pro
     */
    mode?: 'std' | 'pro';
    /**
     * 回调URL
     */
    callback?: string;
  };
}

export interface KlingV15I2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v1_5_i2v
 *
 * Kling AI V1-5 Image-to-Video Generator
 * 
 * Image-to-video generation implementation for Kling AI V1-5 API.
 * 
 * API Documentation: https://app.klingai.com/global/dev/document-api/apiReference/model/imageToVideo
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV15I2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV15I2v({
 *   // 参数见 KlingV15I2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV15I2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingV15I2v(
  params: KlingV15I2vParams,
): Promise<KlingV15I2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v1_5_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV15I2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
